import {OnChangeFn} from "@tanstack/react-table";
import {ReactNode} from "react";
import {Link} from "react-router";

import {SelectOption} from "@/components/select/Select.types.ts";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {TableEmptyStateCta} from "@/components/table/subComponents/TableEmptyStateCta/TableEmptyStateCta.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec, Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {ServiceTag} from "@/lib/types/checks.ts";
import {TagForm} from "@/pages/tags/edit/TagForm.tsx";
import {TagsActionDelete} from "@/pages/tags/list/TagActionDelete.tsx";
import {TagsActionBar} from "@/pages/tags/list/TagsActionBar.tsx";

type TagsTableProps = {
  tags: ServiceTag[];
  actionBar: ReactNode;

  sortColumn: string;
  sortDirection: SortingDirection;
  onSortingChange?: (sorting: Sorting) => void;

  rowSelection: Record<string, boolean>;
  setRowSelection: OnChangeFn<Record<string, boolean>>;

  className?: string;
  isFiltered?: boolean;

  pagination?: LimitAndOffset;
  totalRows?: number;
  updatePagination?: (laf: LimitAndOffset) => void;
  paginationOptions?: SelectOption<number>[];
};

const createFilterString = (id: number): string => {
  return encodeURIComponent(
    JSON.stringify({
      tags: [id.toString()],
    })
  );
};

export const TagsTable = ({
  tags,
  actionBar,
  sortColumn,
  sortDirection,
  onSortingChange,
  rowSelection,
  setRowSelection,
  className,
  isFiltered,
  pagination,
  totalRows,
  updatePagination,
  paginationOptions,
}: TagsTableProps) => {
  const specs: ColumnSpec<ServiceTag>[] = [
    {
      fieldName: "tag_lower",
      fieldTitle: "Name",
      enableSorting: true,
      meta: {
        headerClassName: "w-50",
      },
      accessor: (tag: ServiceTag) => <TagForm key={tag.id} tag={tag} inputFieldClassname={"w-50"} />,
    },
    {
      fieldName: "usage_count",
      fieldTitle: "Checks Tagged",
      enableSorting: true,
      meta: {
        headerClassName: "w-45",
      },
      accessor: (tag: ServiceTag) => (
        <Link to={`/checks?filters=${createFilterString(tag.id)}`} title={"View Associated Checks"}>
          {tag.usage_count}
        </Link>
      ),
    },
    {
      fieldName: "delete",
      fieldTitle: "",
      enableSorting: false,
      meta: {
        headerClassName: "w-5",
      },
      accessor: (tag: ServiceTag) => <TagsActionDelete tag={tag} />,
    },
  ];

  return (
    <Table
      className={className}
      rows={tags}
      specs={specs}
      sorting={{column: sortColumn, direction: sortDirection}}
      onSortingChange={onSortingChange}
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
      actionBar={actionBar}
      noDataComponent={
        <TableEmptyStateCta
          colSpan={specs.length}
          title={isFiltered ? "No Tags found matching the filter(s) above." : "You do not have any Tags."}
          cta={isFiltered ? undefined : <TagsActionBar variant="secondary" />}
        />
      }
      pagination={pagination}
      totalRows={totalRows}
      updatePagination={updatePagination}
      paginationOptions={paginationOptions}
    />
  );
};
